import { apiCustomer } from "@/api"

/** The logged customer's saved order-update choice, as customer-service-gateway keeps it (`/me/communication-preferences`). */
export type CommunicationPreferences = {
  notifyStatusWhatsapp: boolean
}

const PATH = "/me/communication-preferences"

function preferencesOf(raw: unknown): CommunicationPreferences | null {
  if (typeof raw !== "object" || raw === null) return null
  if (!("notifyStatusWhatsapp" in raw) || typeof raw.notifyStatusWhatsapp !== "boolean") return null
  return { notifyStatusWhatsapp: raw.notifyStatusWhatsapp }
}

/** The saved choice, or null when the read fails or the body is unreadable. */
export async function fetchCommunicationPreferences(): Promise<CommunicationPreferences | null> {
  try {
    const { data } = await apiCustomer.get<unknown>(PATH)
    return preferencesOf(data)
  } catch {
    return null
  }
}

/** Saves the choice and returns what the server now keeps; rejects when the request fails. */
export async function saveCommunicationPreferences(preferences: CommunicationPreferences): Promise<CommunicationPreferences | null> {
  const { data } = await apiCustomer.put<unknown>(PATH, { notifyStatusWhatsapp: preferences.notifyStatusWhatsapp })
  return preferencesOf(data)
}

/** The order-update box as shown: the customer's pick on this order, else the saved choice, else on. */
export function orderUpdatesChoice(picked: boolean | undefined, stored: CommunicationPreferences | null | undefined): boolean {
  return picked ?? stored?.notifyStatusWhatsapp ?? true
}

/**
 * Saves the choice of a placed order when it differs from the saved one, or when none is known. Never rejects, so a
 * failed save can never reach the order; resolves to whether the server took it.
 */
export async function persistOrderUpdatesChoice(
  stored: CommunicationPreferences | null | undefined,
  notifyStatusWhatsapp: boolean,
): Promise<boolean> {
  if (stored && stored.notifyStatusWhatsapp === notifyStatusWhatsapp) return false
  try {
    await saveCommunicationPreferences({ notifyStatusWhatsapp })
    return true
  } catch {
    return false
  }
}
